import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Send, UserCheck, XCircle, RotateCcw, RefreshCcw, Search } from "lucide-react";

const ESTADO_LABEL = { bot: "Bot", handoff: "Atendimento humano", encerrada: "Encerrada" };
const ESTADO_COLOR = {
  bot: "bg-emerald-50 text-emerald-800 border-emerald-200",
  handoff: "bg-amber-50 text-amber-900 border-amber-200",
  encerrada: "bg-stone-100 text-stone-600 border-stone-200",
};
const PUBLICO_COLOR = {
  empresa: "bg-indigo-50 text-indigo-800 border-indigo-200",
  grupo: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  individual: "bg-stone-100 text-stone-700 border-stone-200",
  chacara: "bg-teal-50 text-teal-800 border-teal-200",
  evento: "bg-rose-50 text-rose-800 border-rose-200",
};
const INTENT_LABEL = {
  disponibilidade: "Disponibilidade", preco: "Preço", reserva: "Reserva",
  pagamento: "Pagamento", checkin: "Check-in", orcamento: "Orçamento",
  evento: "Evento", humano: "Humano",
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const hoje = new Date();
    const sameDay = d.toDateString() === hoje.toDateString();
    if (sameDay) return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) +
           " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function WhatsAppCentral() {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [texto, setTexto] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [filters, setFilters] = useState({
    estado: "all", publico: "all", intencao: "all", atribuido: "all", busca: "",
  });
  const threadRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = {};
      if (filters.estado !== "all") params.estado = filters.estado;
      if (filters.publico !== "all") params.publico = filters.publico;
      if (filters.intencao !== "all") params.intencao = filters.intencao;
      if (filters.atribuido !== "all") params.atribuido = filters.atribuido;
      if (filters.busca.trim()) params.busca = filters.busca.trim();
      const { data } = await api.get("/whatsapp/conversas", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Erro ao carregar conversas");
    } finally { setLoadingList(false); }
  }, [filters]);

  useEffect(() => { loadList(); }, [loadList]);
  // refresh a cada 15s
  useEffect(() => {
    const t = setInterval(loadList, 15000);
    return () => clearInterval(t);
  }, [loadList]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    setLoadingDetail(true);
    try {
      const [d, m] = await Promise.all([
        api.get(`/whatsapp/conversas/${id}`),
        api.get(`/whatsapp/conversas/${id}/mensagens`, { params: { limit: 100 } }),
      ]);
      setDetail(d.data);
      setMessages(m.data.items || []);
    } catch {
      toast.error("Erro ao abrir conversa");
    } finally { setLoadingDetail(false); }
  }, []);

  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  const assumir = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/whatsapp/conversas/${selectedId}/assumir`);
      toast.success("Você assumiu esta conversa");
      await loadDetail(selectedId); await loadList();
    } catch (e) {
      const err = e?.response?.data?.detail;
      if (err && err.erro === "CONVERSA_JA_ASSUMIDA")
        toast.error(`Já assumida por ${err.operador_atual || "outro operador"}`);
      else toast.error("Não foi possível assumir");
    }
  };
  const enviar = async () => {
    const t = texto.trim();
    if (!t || !selectedId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/whatsapp/conversas/${selectedId}/mensagens`, { texto: t });
      setTexto("");
      if (data.dry_run) toast.info("Modo de teste — mensagem não enviada ao WhatsApp.");
      else toast.success("Mensagem enviada");
      await loadDetail(selectedId); await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar");
    } finally { setSending(false); }
  };
  const encerrar = async () => {
    if (!selectedId) return;
    if (!window.confirm("Encerrar esta conversa?")) return;
    try {
      await api.post(`/whatsapp/conversas/${selectedId}/encerrar`);
      toast.success("Conversa encerrada");
      await loadDetail(selectedId); await loadList();
    } catch { toast.error("Erro ao encerrar"); }
  };
  const devolverBot = async () => {
    if (!selectedId) return;
    if (!window.confirm("Devolver esta conversa ao bot?")) return;
    try {
      await api.post(`/whatsapp/conversas/${selectedId}/devolver-bot`);
      toast.success("Devolvido ao bot");
      await loadDetail(selectedId); await loadList();
    } catch { toast.error("Erro ao devolver ao bot"); }
  };

  const myEmail = (me?.email || "").toLowerCase();
  const canReply = detail?.estado === "handoff" &&
    (detail?.operador_email || "").toLowerCase() === myEmail;

  return (
    <div data-testid="whatsapp-central" className="min-h-[calc(100vh-8rem)]">
      <div className="mb-6">
        <h1 className="font-serif text-3xl text-stone-900 mb-1 flex items-center gap-2">
          <MessageCircle className="text-[#083A6B]" size={28} /> Central WhatsApp
        </h1>
        <p className="text-sm text-stone-500">
          Atendimento humano de conversas — envio real desabilitado (modo teste).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr_320px] gap-4 h-[calc(100vh-12rem)]">
        {/* COLUNA 1 — FILA */}
        <aside className="bg-[#FFFFFF] border border-stone-200 rounded-lg flex flex-col overflow-hidden">
          <div className="p-3 border-b border-stone-200 space-y-2">
            <div className="flex items-center gap-2">
              <Search size={14} className="text-stone-400" />
              <Input data-testid="whatsapp-busca" value={filters.busca}
                onChange={(e) => setFilters({ ...filters, busca: e.target.value })}
                placeholder="Nome ou telefone..." className="h-8 text-sm" />
              <button data-testid="whatsapp-refresh" onClick={loadList}
                className="p-1 text-stone-500 hover:text-stone-900" title="Atualizar">
                <RefreshCcw size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={filters.estado} onValueChange={(v) => setFilters({ ...filters, estado: v })}>
                <SelectTrigger data-testid="whatsapp-filtro-estado" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos estados</SelectItem>
                  <SelectItem value="bot">Bot</SelectItem>
                  <SelectItem value="handoff">Handoff</SelectItem>
                  <SelectItem value="encerrada">Encerradas</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.atribuido} onValueChange={(v) => setFilters({ ...filters, atribuido: v })}>
                <SelectTrigger data-testid="whatsapp-filtro-atribuido" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas atribuições</SelectItem>
                  <SelectItem value="me">Minhas</SelectItem>
                  <SelectItem value="unassigned">Não atribuídas</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.publico} onValueChange={(v) => setFilters({ ...filters, publico: v })}>
                <SelectTrigger data-testid="whatsapp-filtro-publico" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos públicos</SelectItem>
                  <SelectItem value="empresa">Empresa</SelectItem>
                  <SelectItem value="grupo">Grupo</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="chacara">Chácara</SelectItem>
                  <SelectItem value="evento">Evento</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.intencao} onValueChange={(v) => setFilters({ ...filters, intencao: v })}>
                <SelectTrigger data-testid="whatsapp-filtro-intencao" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas intenções</SelectItem>
                  {Object.entries(INTENT_LABEL).map(([k, v]) =>
                    <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div data-testid="whatsapp-conversa-lista" className="flex-1 overflow-y-auto">
            {loadingList && items.length === 0 ? (
              <div className="p-6 text-center text-stone-400 text-sm">Carregando…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-stone-400 text-sm">Nenhuma conversa</div>
            ) : items.map((c) => {
              const active = c.id === selectedId;
              return (
                <button key={c.id} data-testid={`whatsapp-conversa-item-${c.id}`}
                  onClick={() => setSelectedId(c.id)}
                  className={`w-full text-left px-3 py-3 border-b border-stone-100 hover:bg-stone-50 transition ${active ? "bg-[#EEE8DF]" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm text-stone-900 truncate">{c.contato?.nome || c.contato?.telefone_mascarado || "Sem nome"}</div>
                    <span className="text-[10px] text-stone-400 shrink-0">{fmtTime(c.ultima_atividade)}</span>
                  </div>
                  <div className="text-xs text-stone-500 truncate">{c.contato?.telefone_mascarado}</div>
                  <div className="text-xs text-stone-600 mt-1 truncate">{c.ultima_mensagem?.texto || "—"}</div>
                  <div className="flex flex-wrap items-center gap-1 mt-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${ESTADO_COLOR[c.estado] || ""}`}>{ESTADO_LABEL[c.estado] || c.estado}</span>
                    {c.publico && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PUBLICO_COLOR[c.publico] || ""}`}>{c.publico}</span>}
                    {c.intencao && <span className="text-[10px] px-1.5 py-0.5 rounded border bg-stone-50 text-stone-700 border-stone-200">{INTENT_LABEL[c.intencao] || c.intencao}</span>}
                    {c.nao_lidas > 0 && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-[#D4A652] text-white font-semibold">{c.nao_lidas}</span>}
                  </div>
                  {c.operador_email && (
                    <div className="text-[10px] text-stone-500 mt-1 truncate">👤 {c.operador_email}</div>
                  )}
                </button>
              );
            })}
          </div>
        </aside>

        {/* COLUNA 2 — THREAD */}
        <section className="bg-[#FFFFFF] border border-stone-200 rounded-lg flex flex-col overflow-hidden min-w-0">
          {!detail ? (
            <div className="flex-1 flex items-center justify-center text-stone-400 text-sm">Selecione uma conversa à esquerda</div>
          ) : (
            <>
              <header className="p-3 border-b border-stone-200 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-stone-900 truncate">{detail.contato?.nome || detail.contato?.telefone_mascarado}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-2">
                    <span>{detail.contato?.telefone_mascarado}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${ESTADO_COLOR[detail.estado] || ""}`}>{ESTADO_LABEL[detail.estado] || detail.estado}</span>
                    {detail.operador_email && <span className="text-[10px]">👤 {detail.operador_email}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {detail.estado === "bot" && !detail.operador_email && (
                    <Button data-testid="whatsapp-assumir" onClick={assumir} size="sm"
                      className="bg-[#083A6B] hover:bg-[#243d30] text-white gap-1">
                      <UserCheck size={14} /> Assumir
                    </Button>
                  )}
                  {detail.estado === "handoff" && (
                    <>
                      <Button data-testid="whatsapp-devolver-bot" onClick={devolverBot} size="sm" variant="outline" className="gap-1">
                        <RotateCcw size={14} /> Devolver ao bot
                      </Button>
                      <Button data-testid="whatsapp-encerrar" onClick={encerrar} size="sm" variant="outline"
                        className="gap-1 text-[#D4A652] border-[#D4A652]/40">
                        <XCircle size={14} /> Encerrar
                      </Button>
                    </>
                  )}
                </div>
              </header>

              {detail.estado === "handoff" && (
                <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-900">
                  Atendimento humano em andamento. A IA está pausada nesta conversa.
                </div>
              )}

              <div ref={threadRef} data-testid="whatsapp-thread" className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F5F1E8]">
                {loadingDetail && messages.length === 0 ? (
                  <div className="text-center text-stone-400 text-sm">Carregando…</div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-stone-400 text-sm">Sem mensagens</div>
                ) : messages.map((m) => {
                  const isOut = m.direcao === "out";
                  const isOp = m.origem === "operador";
                  const isAI = !isOp && isOut;
                  return (
                    <div key={m.id} data-testid={`whatsapp-msg-${m.id}`}
                      className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                        isOp ? "bg-[#083A6B] text-white" :
                        isAI ? "bg-white text-stone-800 border border-stone-200" :
                        "bg-white text-stone-800 border border-stone-200"}`}>
                        <div className="text-[10px] mb-0.5 opacity-70 flex items-center gap-1">
                          {isOp ? `Operador · ${m.operador_email || ""}` : isAI ? "IA" : "Cliente"}
                          <span>· {fmtTime(m.criada_em)}</span>
                          {m.dry_run && <span className="ml-1 uppercase tracking-wider">· teste</span>}
                        </div>
                        <div className="whitespace-pre-wrap break-words">{m.texto}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <footer className="border-t border-stone-200 p-3 bg-white">
                {!canReply ? (
                  <div className="text-xs text-stone-500 text-center">
                    {detail.estado === "encerrada" ? "Conversa encerrada." :
                     detail.estado === "bot" ? "Assuma esta conversa para enviar mensagens." :
                     "Esta conversa está com outro operador."}
                  </div>
                ) : (
                  <>
                    <Textarea data-testid="whatsapp-mensagem-input" value={texto}
                      onChange={(e) => setTexto(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); enviar(); }
                      }}
                      placeholder="Digite sua resposta... (Ctrl+Enter para enviar)"
                      className="min-h-[70px] resize-none text-sm mb-2" maxLength={4096} />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-stone-400">
                        Modo de teste — mensagem não enviada ao WhatsApp real.
                      </span>
                      <Button data-testid="whatsapp-enviar" onClick={enviar}
                        disabled={!texto.trim() || sending}
                        className="bg-[#083A6B] hover:bg-[#243d30] text-white gap-1" size="sm">
                        <Send size={14} /> {sending ? "Enviando..." : "Enviar (Ctrl+Enter)"}
                      </Button>
                    </div>
                  </>
                )}
              </footer>
            </>
          )}
        </section>

        {/* COLUNA 3 — CONTEXTO */}
        <aside data-testid="whatsapp-contexto" className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-y-auto p-4">
          <h2 className="font-serif text-lg text-stone-900 mb-3">Contexto comercial</h2>
          {!detail ? (
            <div className="text-sm text-stone-400">Selecione uma conversa.</div>
          ) : (
            <div className="space-y-4 text-sm">
              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Contato</div>
                <div className="text-stone-900 font-medium">{detail.contato?.nome || "Não identificado"}</div>
                <div className="text-stone-600 text-xs">{detail.contato?.telefone_mascarado}</div>
                <div className="text-[10px] text-stone-500 mt-1">
                  Opt-in: {detail.contato?.opt_in === true ? "✔" : detail.contato?.opt_in === false ? "recusado" : "não confirmado"}
                </div>
                <div className="text-[10px] text-stone-500">
                  Janela 24h: {detail.janela_aberta ? "aberta" : "fechada"}
                </div>
              </section>

              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Classificação</div>
                <div className="flex flex-wrap gap-1">
                  {detail.publico ? <Badge variant="outline">{detail.publico}</Badge> : <span className="text-stone-400 text-xs">público não identificado</span>}
                  {detail.intencao && <Badge variant="outline">{INTENT_LABEL[detail.intencao] || detail.intencao}</Badge>}
                </div>
              </section>

              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Hóspedes vinculados</div>
                {(detail.contexto_comercial?.hospedes || []).length === 0 ? (
                  <div className="text-stone-400 text-xs">Nenhum hóspede identificado por este telefone.</div>
                ) : detail.contexto_comercial.hospedes.map((h) => (
                  <div key={h.id} className="text-stone-800 text-xs">{h.nome}</div>
                ))}
              </section>

              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Reservas</div>
                {(detail.contexto_comercial?.reservas || []).length === 0 ? (
                  <div className="text-stone-400 text-xs">Nenhuma reserva vinculada.</div>
                ) : detail.contexto_comercial.reservas.map((r) => (
                  <div key={r.id} className="border border-stone-200 rounded p-2 mb-1 text-xs bg-white">
                    <div className="font-medium">{r.data_entrada} → {r.data_saida}</div>
                    <div className="text-stone-500">Status: {r.status} · Sinal: {r.sinal_status || "—"}</div>
                    <div className="text-stone-500">Total: R$ {r.valor_total || 0} · Pago: R$ {r.valor_pago || 0}</div>
                  </div>
                ))}
              </section>

              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Orçamentos</div>
                {(detail.contexto_comercial?.orcamentos || []).length === 0 ? (
                  <div className="text-stone-400 text-xs">Nenhum orçamento vinculado.</div>
                ) : detail.contexto_comercial.orcamentos.map((o) => (
                  <div key={o.id} className="text-xs text-stone-800">
                    #{o.numero || o.id.slice(0, 6)} · {o.status} · R$ {o.valor_total || 0}
                  </div>
                ))}
              </section>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
